import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import DashboardLayout from "../components/DashboardLayout";
import PatientListColumn from "../components/PatientListColumn";
import PatientsHome from "../components/PatientsHome";
import PatientProgressPage from "./PatientProgressPage";
import { Button } from "../components/ui/button";
import { ArrowLeft } from "lucide-react";
import { API } from "../App";

const STORAGE_KEY = "kintsugi_patient_list_collapsed";

const PatientsWorkspacePage = ({ user, logout }) => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(STORAGE_KEY) === "1");
  const [refreshKey, setRefreshKey] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [mobileHome, setMobileHome] = useState(false); // iPhone : afficher l'accueil (patients en attente, demandes) à la place de la liste
  useEffect(() => { if (patientId) setMobileHome(false); }, [patientId]);

  // Le repli (avatars seuls) n'a de sens qu'à partir de md (iPad) : sur iPhone la liste occupe tout l'écran et reste toujours dépliée
  const [isMd, setIsMd] = useState(() => window.matchMedia("(min-width: 768px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const onChange = (e) => setIsMd(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  const effectiveCollapsed = collapsed && isMd;

  useEffect(() => { localStorage.setItem(STORAGE_KEY, collapsed ? "1" : "0"); }, [collapsed]);
  useEffect(() => {
    // iPad / écrans moyens : la liste se replie automatiquement quand un patient est ouvert pour laisser la place au contenu
    if (patientId && window.matchMedia("(min-width: 768px) and (max-width: 1279px)").matches) setCollapsed(true);
  }, [patientId]);
  useEffect(() => {
    axios.get(`${API}/therapist/patients/pending`, { withCredentials: true }).then((r) => setPendingCount(r.data.length)).catch(() => {});
  }, [refreshKey]);

  const goHome = () => { navigate("/therapist/patients"); setMobileHome(true); };
  const showPane = !!patientId || mobileHome;

  return (
    <DashboardLayout user={user} logout={logout} fullBleed>
      <div className="flex h-[calc(100vh-3.5rem)] lg:h-screen" data-testid="patients-workspace">
        <aside className={`${showPane ? "hidden md:flex" : "flex"} ${effectiveCollapsed ? "md:w-16" : "md:w-72 xl:w-80"} w-full flex-col border-r bg-card/40 shrink-0 transition-[width] duration-200`} data-testid="patients-list-pane">
          <PatientListColumn selectedId={patientId} onSelect={(id) => navigate(`/therapist/patients/${id}`)} onHome={goHome}
            collapsed={effectiveCollapsed} onToggleCollapsed={() => setCollapsed((c) => !c)} refreshKey={refreshKey} pendingCount={pendingCount} />
        </aside>
        <section className={`${showPane ? "flex" : "hidden md:flex"} flex-1 min-w-0 flex-col overflow-y-auto overflow-x-hidden`} data-testid="patients-workspace-pane">
          <div className="px-4 py-4 sm:px-6 lg:px-8">
            {patientId ? (
              <>
                <Button variant="ghost" size="sm" onClick={() => { setMobileHome(false); navigate("/therapist/patients"); }} className="md:hidden mb-3 -ml-2 gap-1.5" data-testid="back-to-patients-btn"><ArrowLeft className="w-4 h-4" /> Patients</Button>
                <PatientProgressPage key={patientId} user={user} logout={logout} embedded patientIdProp={patientId} onPatientChanged={() => setRefreshKey((k) => k + 1)} />
              </>
            ) : (
              <>
                <Button variant="ghost" size="sm" onClick={() => setMobileHome(false)} className="md:hidden mb-3 -ml-2 gap-1.5" data-testid="back-to-patient-list-btn"><ArrowLeft className="w-4 h-4" /> Liste des patients</Button>
                <PatientsHome onPatientsChanged={() => setRefreshKey((k) => k + 1)} onPendingCount={setPendingCount} />
              </>
            )}
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
};

export default PatientsWorkspacePage;
